import React from 'react';

const MONTH_ABBR = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Turn a range.js period key back into "Mar 2026". */
function formatPeriod(period) {
  if (period == null) return '';
  const year = Math.floor((period - 1) / 12);
  const month = period - year * 12;
  return `${MONTH_ABBR[month]} ${year}`;
}

function Row({ label, weight, score, detail }) {
  if (score == null) {
    return (
      <div className="flex items-baseline justify-between gap-2 py-1">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-xs text-gray-400">No data</span>
      </div>
    );
  }

  return (
    <div className="py-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-gray-600">
          {label}
          {weight != null && <span className="text-gray-400"> · {Math.round(weight * 100)}%</span>}
        </span>
        <span className="text-xs font-semibold text-text">{Math.round(score)}</span>
      </div>
      {detail && <div className="text-[11px] text-gray-400 leading-tight">{detail}</div>}
    </div>
  );
}

/**
 * What went into a CScore.
 *
 * Works for two shapes, because the number it explains differs by page:
 *  - a monthly clinician_data_record (calc_* fields), on the month tables
 *  - an averageBreakdown() roll-up, on the "Average CScore" list columns
 *
 * `weights` comes from global settings; the percentages shown are the weights
 * actually applied, which is what makes the three component scores add up to
 * the headline figure.
 */
export default function ScoreBreakdown({ breakdown, score, weights = {}, title = 'Score breakdown' }) {
  if (!breakdown) return null;

  // Monthly records prefix the component scores with calc_; the roll-up does not.
  const retention = breakdown.retention_score ?? breakdown.calc_retention_score;
  const caseloadScore = breakdown.caseload_score ?? breakdown.calc_caseload_score;
  const attendanceScore = breakdown.attendance_score ?? breakdown.calc_attendance_score;

  const months = breakdown.month_count;
  const span = breakdown.first_period != null && breakdown.last_period != null
    ? `${formatPeriod(breakdown.first_period)} – ${formatPeriod(breakdown.last_period)}`
    : null;

  const pct = (v) => (v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—');
  const num = (v, digits = 1) => (v != null ? Number(v).toFixed(digits) : '—');

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h4 className="text-sm font-bold text-text">{title}</h4>
        {score != null && <span className="text-sm font-bold text-primary">{Math.round(score)}</span>}
      </div>

      <div className="divide-y divide-gray-100">
        <Row
          label="Attendance"
          weight={weights.attendance}
          // The attendance component is stored 0-1 while the other two are
          // 0-100, so it has to be scaled before it can sit in the same column.
          score={attendanceScore != null ? attendanceScore * 100 : null}
          detail={`${pct(breakdown.attendance_pct)} attended`}
        />
        <Row
          label="Caseload"
          weight={weights.caseload}
          score={caseloadScore}
          detail={`${num(breakdown.caseload)} average cases`}
        />
        <Row
          label="Retention"
          weight={weights.retention}
          score={retention}
          detail={`${num(breakdown.discharges, 0)} discharges · ${num(breakdown.length_of_stay, 0)} day stay`}
        />
      </div>

      {months > 1 && (
        <p className="text-[11px] text-gray-400 mt-2 pt-2 border-t border-gray-100 leading-tight">
          Averaged over {months} months{span ? ` (${span})` : ''}
        </p>
      )}
    </div>
  );
}
